import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { authHeaders, errorMessage, unwrap } from 'api/call'
import {
  deleteReportsByIdDeleteTarget,
  getReports,
  getReportsById,
  patchReportsById,
  type Report,
  type ReportsIndexResponse,
} from 'api/generated'
import { toast } from 'sonner'

interface ReportsState {
  loading: boolean
  error: string | null
  report: Report | null
  reports: Report[] | null
  page: number
  totalPages: number
}

const initialState: ReportsState = {
  loading: false,
  error: null,
  report: null,
  reports: null,
  page: 1,
  totalPages: 0,
}

export const fetchReports = createAsyncThunk<
  ReportsIndexResponse,
  number,
  { rejectValue: string }
>('reports/fetchReports', async (page, { rejectWithValue }) => {
  try {
    return await unwrap(getReports({ headers: authHeaders(), query: { page } }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch reports.'))
  }
})

export const fetchReport = createAsyncThunk<
  Report,
  number,
  { rejectValue: string }
>('reports/fetchReport', async (id, { rejectWithValue }) => {
  try {
    return await unwrap(
      getReportsById({ headers: authHeaders(), path: { id } })
    )
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch report.'))
  }
})

export const deleteTarget = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>('reports/deleteTarget', async (id, { dispatch, rejectWithValue }) => {
  try {
    await unwrap(
      deleteReportsByIdDeleteTarget({ headers: authHeaders(), path: { id } })
    )
    toast.success('Segnalazione aggiornata con successo')
    window.location.href = '/segnalazioni'
    dispatch(fetchReports(1))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to delete target.'))
  }
})

export const updateReport = createAsyncThunk<
  void,
  { id: number; admin_notes: string },
  { rejectValue: string }
>('reports/updateReport', async ({ id, admin_notes }, { rejectWithValue }) => {
  try {
    await unwrap(
      patchReportsById({
        headers: authHeaders(),
        path: { id },
        body: { admin_notes },
      })
    )
    toast.success('Segnalazione aggiornata con successo')
    window.location.href = '/segnalazioni'
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to update report.'))
  }
})

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = action.payload.total_pages
        state.page = action.payload.page
        state.reports = action.payload.data
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch reports.'
      })
      .addCase(fetchReport.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.loading = false
        state.report = action.payload
      })
      .addCase(fetchReport.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch report.'
      })
      .addCase(deleteTarget.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTarget.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteTarget.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete target.'
      })
      .addCase(updateReport.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateReport.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateReport.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update report.'
      })
  },
})

export const reportsReducer = reportsSlice.reducer
